import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import MenuIcon from "../icons";
import {
  useRequest,
  apis,
  getRemoteModuleUrl,
  REMOTE_TAG,
  resolveMenuItem,
} from "../utils";
import {
  cleanTree,
  flat2TreeWrapper,
  sortTree,
  removeEmptyNodes,
} from "../utils";
import { useTranslation } from "react-i18next";
import type {
  AppearanceProps,
  AppPermissionItem,
  AuthRes,
  RenderModule,
} from "../dto";
import { PUBLIC_CAPACITY_ID, type IMenuResult, type MenuItem } from "./dto";

interface IMenuParams {
  /** 菜单是否国际化 */
  isI18N?: boolean;
  /** xx中心的路径前缀 */
  activeCenter?: string;
  /** 中心列表整体 */
  serviceList?: AppearanceProps["serviceList"];
  /** 自定义侧边栏属性 */
  aside: RenderModule["aside"];
}

/** 菜单数据获取与操作hook定义 */
type IUseMenuType = (
  app?: AppearanceProps["app"],
  params?: IMenuParams
) => [IMenuResult, React.Dispatch<React.SetStateAction<MenuItem[]>>];

/**
 * 获取菜单
 */
const useMenu: IUseMenuType = (app, params) => {
  const { activeCenter, isI18N = false, aside } = params;
  const { icons, customMenu = false } = aside || {};
  const { t } = useTranslation();
  /** 存储构建完成的菜单 */
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [fullMenu, setFullMenu] = useState<MenuItem[]>([]);
  const [authMenuList, setAuthMenuList] = useState<AppPermissionItem[]>([]);
  const [permissionList, setPermissionList] = useState<string[]>(null);
  // 公共权限列表，仅在组件内部处理
  const [publicPermissionList, setPublicPermissionList] = useState<string[]>(
    []
  );
  const { pathname } = useLocation();

  const [, fetchAppAuth] = useRequest<any, AuthRes>(apis.applications.show);
  const [, fetchUserAuth] = useRequest<any, AuthRes>(apis.user.permission);

  /**
   * 获取用户权限,用来过滤菜单
   */
  const getPermission = async () => {
    const res: string[] = [];
    const { dataList } = (await fetchUserAuth()) || {};

    if (Array.isArray(dataList)) {
      const publicCapacityItem = dataList.find(
        (m) => m.componentId === PUBLIC_CAPACITY_ID
      );
      const publicPermission = dataList.filter(
        (m) => m.parentId === publicCapacityItem?.id
      );
      setPublicPermissionList(publicPermission.map((m) => m.componentId));

      dataList
        .filter((m) => m.appCode === app)
        .forEach((m) => {
          res.push(m.componentId);
        });
    }
    setPermissionList(res.filter((m) => m));
    return res;
  };
  /**
   * 获取系统应用权限，用来构建菜单
   */
  const getAppPermission = async (appCode: string) => {
    let res: AppPermissionItem[] = [];
    const { dataList: appPremission } = (await fetchAppAuth({ appCode })) || {};

    if (Array.isArray(appPremission)) {
      const authPermission = aside?.getUserPermission
        ? await aside.getUserPermission()
        : await getPermission();

      res = appPremission.filter((m) => {
        return authPermission.includes(m.componentId);
      });
    }

    return res;
  };

  const init = async (appcode: string) => {
    if (customMenu) {
      // 自定义菜单时不进行内容的初始化
      return;
    }
    if (!appcode) {
      console.warn(
        `${appcode} is not vaild app, Appearance permissions can not init`
      );
      return;
    }
    const data = aside?.getAppPermission
      ? await aside.getAppPermission(appcode)
      : await getAppPermission(appcode);

    const resolvedData = data.map((d) => resolveMenuItem(d));

    setAuthMenuList(resolvedData);
  };

  /**
   * 构建菜单
   */
  const getMenu = useCallback(async () => {
    /* 存放树形结构数据 */
    let res = [] as MenuItem[];

    /** 左侧构建菜单只取页面、目录、分组 三种类型 */
    const authMenuListForRender = authMenuList.filter((m) =>
      [0, 3, 4].includes(m.componentType)
    );

    if (authMenuListForRender.length) {
      res = authMenuListForRender.map((m) => {
        return {
          label: (
            <span className="wbuc-menu-title-content" style={{ flex: 1 }}>
              <span className="wbuc-menu-title-content-inner">
                {isI18N && m.i18nKey ? t(m.i18nKey) : m.name}
              </span>
            </span>
          ),
          labelstring: isI18N && m.i18nKey ? t(m.i18nKey) : m.name,
          key: m.componentId,
          icon: MenuIcon(m.componentId, icons) || "",
          indent: 20,
          id: m.id,
          parentId: m.parentId,
          orderIdx: m.orderIdx,
          type: m.componentType === 4 ? "group" : "",
          title: m.name,
          cid: m.componentId,
          curl: m.componentUrl,
          componenttype: m.componentType,
        };
      });

      setFullMenu([...res]);
      // add title map
      res = flat2TreeWrapper<MenuItem>(res, { key: "key", title: "title" });
      // 去除没有子节点的目录和分组;
      res = removeEmptyNodes(res, (m) => m.componenttype === 0);
      res = sortTree(res, "orderIdx");
      res = cleanTree(res);
    }
    setMenu(res);
  }, [activeCenter, pathname, authMenuList]);

  useEffect(() => {
    if (!authMenuList.length) {
      return;
    }
    getMenu();
  }, [authMenuList, activeCenter]);

  useEffect(() => {
    init(app);
  }, [app]);

  return [
    { menu, authMenuList, permissionList, fullMenu, publicPermissionList },
    setMenu,
  ];
};

export default useMenu;
export { default as useLogout } from "./use-logout";
